import { Search } from "@/src/scripts/components/search/SearchTypes";
import ComponentBase from "@components/ComponentBase";
import { ComponentElem } from "@components/interfaces";
import CollapseWrapper from "../ui/CollapseWrapper";
import { Emitter } from "strict-event-emitter";
import schema from "@schema/search/search.json";
import ApiProxy from "../../ts/ApiProxy";
import ResultBlock from "./ResultBlock";
import Tools from "../../ts/Tools";
import GS from "../../ts/GlobalSetting";
import Pager from "../ui/Pager";
import { Common } from "../common/CommonTypes";
import { AutoLoadMark } from "../../ts/AutoLoadMark";
import { gsap } from "gsap";
import EZAlert from "../popups/EZAlert";

type STATE = "idle" | "loading";
type ADD_MODE = "replace" | "add";

export default class SearchResult extends ComponentBase {
  private state: STATE = "idle";
  private _lastRequestAddMode: ADD_MODE;
  private resultCollapse: CollapseWrapper;
  private _resultList: Search.Result[];
  private _headerData: Search.HeaderData;
  private _pager: Pager;
  autoLoadMark: AutoLoadMark;
  private _pagingInfo: Common.PagingInfo;
  emitter: Emitter<{ apiLoaded: [], resultUpdated: [totalCount: number, headerData: Search.HeaderData], pageIndexChange: [pageIndex: number] }> = new Emitter();
  constructor(elem: ComponentElem) {
    super(elem);
    this.autoLoadMark = new AutoLoadMark(Math.max(300, window.innerHeight));
    let pageData: Search.PageData = ApiProxy.getPageData();
    this._pagingInfo = pageData.result_paging_info;
    this.$root.append(this.autoLoadMark.$root);
  }

  init() {
    this.resultCollapse = this.seekComponentByClass(`result-collapse`) as CollapseWrapper;
    this._pager = this.seekComponentByName("pager") as Pager;
    let pagingInfo: Common.PagingInfo = ApiProxy.getPageData().result_paging_info,
      pageSize: number = pagingInfo.page_size,
      pageIndex: number = Math.floor(pagingInfo.start_index / pageSize);
    this._pager.reset(pageSize, pagingInfo.total_count, pageIndex);
    // bind listener after reset
    this._pager.emiiter.on("change", (pageIndex: number) => {
      if (this.state !== "idle")
        throw new Error(`unexpected error: pager 事件應該不能在非 idle 狀態中被觸發`);
      this.emitter.emit("pageIndexChange", pageIndex);
    });
  }

  getPager() {
    return this._pager;
  }

  toggle() {
    let newState = this.state === "idle" ? "loading" : "idle";
    this.setState(newState as STATE);
  }

  toggleAutoLoad(b: boolean) {
    if (b) {
      if (this.getNextPageIndex() !== undefined) this.autoLoadMark.toggle(true);
    } else {
      this.autoLoadMark.toggle(false);
    }
  }

  request(input: Search.SearchInput, addMode: ADD_MODE = "replace") {
    if (this.state === "loading")
      return false;
    console.log(`開始新的搜尋請求`);
    this._lastRequestAddMode = addMode;
    this.setState("loading");
    this._pager.toggleLocking(true);
    setTimeout(() => {
      this.callApi(input);
    }, 500);
    return true;
  }

  insertRequest(input: Search.SearchInput) {
    console.log(`插入新的搜尋請求`);
    console.log(input);
    this._lastRequestAddMode = "replace";
    this.callApi(input);
  }

  callApi(input: Search.SearchInput) {
    console.log(`search call api`);
    // 創造新副本供異步調用
    let params: any = input = Object.assign({}, input);
    let exampleIndex = 2;
    if (GS.setting.testEnv) {
      if (input.keyword === "無資料")
        exampleIndex = 3;
      else if (input.sort)
        exampleIndex = 0;
      else if (input.site_list && input.site_list.length === 1)
        exampleIndex = 1;
    }

    ApiProxy.callApi("search/search", params, { schema: schema, exampleIndex: exampleIndex, simDelay: 500 })
      .then((result: { data: Search.SearchApiResult }) => {
        let data = result.data;
        if (GS.setting.testEnv && input.keyword === "分頁測試")
          data = generateTestData(input);
        this._headerData = data.header;
        this._resultList = data.data_list;
        this._pagingInfo = data.paging_info;
        this.emitter.emit("apiLoaded");
      }).catch((error) => {
        console.error(error);
        EZAlert.alert(error.message);
      });
  }

  updateResults(queryUrl: string) {
    let pageIndex = Math.floor(this._pagingInfo.start_index / this._pagingInfo.page_size);
    this._pager.reset(this._pagingInfo.page_size, this._pagingInfo.total_count, pageIndex, queryUrl);
    let $container = this.$root.find(".results");
    if (this._lastRequestAddMode === "replace")
      $container.empty();
    this._resultList.forEach((data, index) => {
      let resultBlock = ResultBlock.generate(data);
      if (this._lastRequestAddMode === "add") {
        gsap.set(resultBlock.$root, { opacity: 0 });
        gsap.to(resultBlock.$root, { duration: .3, opacity: 1, delay: (.1 * index) });
      }
      $container.append(resultBlock.$root);
    });
    this._pager.toggleLocking(false);
    this.setState("idle");
    this.checkResults();
  }

  checkResults() {
    this.$root.attr("no-result", (this._pagingInfo.total_count > 0) ? "false" : "true");
    this.emitter.emit("resultUpdated", this._pagingInfo.total_count, this._headerData);
  }

  getNextPageIndex() {
    return this._pager.getNextPageIndex();
  }

  getState() {
    return this.state;
  }

  setState(newState: STATE) {
    if (this.state === newState)
      return;
    this.state = newState;
    this.$root.attr("state", this.state);
    if (this.state === "idle") {
      this.resultCollapse.toggle(false);
    } else if (this.state === "loading" && this._lastRequestAddMode === "replace") {
      Tools.scrollToElement("on-search-mark");
      this.resultCollapse.toggle(true);
    }
  }
}

function generateTestData(input: Search.SearchInput) {
  const totalCount = 48;
  let pageIndex = input.page_index | 0,
    pageSize = GS.search.pageSize,
    startIndex = pageIndex * pageSize,
    endIndex = startIndex + pageSize;
  if (endIndex > totalCount)
    endIndex = totalCount;
  let data: Search.SearchApiResult = {
    paging_info: {
      start_index: startIndex,
      total_count: totalCount,
      page_size: pageSize
    },
    data_list: [],
    header: {
      "title": "探索未知的旅程",
      "sub_title": "說走就走 也是最燦爛的自由",
      "picture": {
        "pc": "./static/images/search/header/keyword-1.png",
        "mobile": "./static/images/search/header/keyword-1.m.png"
      }
    }
  };


  for (let i = startIndex; i < endIndex; i++) {
    let result: Search.Result = {
      "title": `資料 ${(i + 1)}`,
      "href": "./product.html",
      "description": "世人稱她「17歲少女之湖」、「夢幻湖」，這裡四面環山，平常湖面總有薄紗般的雲霧籠罩著，在冬季飽水期時，宛如遺落在山區的一面明鏡，可欣賞藍天倒影、日出等美景； 若遇陰雨天或是午後，湖面上常會蓋著一層矇矓的薄紗，讓人難以",
      "tags": [
        "6人成團"
      ],
      "rating": {
        "score": 2.5,
        "count": 500
      },
      "picture": {
        "pc": "./static/images/search/result/3.png",
        "mobile": "./static/images/search/result/3.m.png"
      }
    }
    data.data_list.push(result);
  }

  return data;
}